import React, { useState, useRef, useCallback } from "react";
import styles from "./index.module.css";
import SendIcon from "@mui/icons-material/Send";
import AddCommentIcon from "@mui/icons-material/AddComment";
import IconButton from "@mui/material/IconButton";
import { axios } from "../../axios";
import { getStoredToken } from "../../services/authService";

/**
 * Renders a text string that may contain **bold** markdown into React elements.
 */
function RenderText({ text }) {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("**") && part.endsWith("**")
          ? <strong key={i}>{part.slice(2, -2)}</strong>
          : part
      )}
    </>
  );
}

/**
 * AIDialog — streams AI responses from the server via POST /api/ai/question.
 * Displays conversation as chat bubbles (user right, assistant left).
 *
 * Props:
 *   westernDateTime – ISO datetime string for the birth date (WesternDateTime)
 *   isMale          – boolean gender flag (IsMale)
 *   historyId       – optional seed conversation history id (HistoryId)
 */
export default function AIDialog({ westernDateTime, isMale, historyId: historyIdProp }) {
  const [question, setQuestion] = useState("");
  // messages: [{role: 'user'|'assistant', text: string}]
  const [messages, setMessages] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isWaiting, setIsWaiting] = useState(false);
  const [error, setError] = useState("");
  const [localHistoryId, setLocalHistoryId] = useState(null);
  const abortRef = useRef(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleNewChat = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setLocalHistoryId(null);
    setMessages([]);
    setQuestion("");
    setError("");
    setIsStreaming(false);
    setIsWaiting(false);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!question.trim() || isStreaming) return;

    const userText = question.trim();

    // Abort previous request if any
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    // Append user message, then a placeholder assistant message
    setMessages(prev => [
      ...prev,
      { role: "user", text: userText },
      { role: "assistant", text: "" },
    ]);
    setQuestion("");
    setError("");
    setIsStreaming(true);
    setIsWaiting(true);

    try {
      const baseURL = (axios.defaults.baseURL || "/").replace(/\/$/, "");
      const headers = {
        "Content-Type": "application/json",
      };
      const token = getStoredToken();
      if (token) {
        headers["X-Session-Token"] = token;
      }
      const res = await fetch(`${baseURL}/api/ai/question`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          WesternDateTime: westernDateTime || "",
          IsMale: isMale ?? true,
          HistoryId: localHistoryId ?? historyIdProp ?? null,
          UserChatMessage: userText,
        }),
        signal: controller.signal,
      });

      if (!res.ok) {
        setError(`Server error: ${res.status}`);
        setIsStreaming(false);
        setIsWaiting(false);
        return;
      }

      // If plain JSON (e.g. "not relevant" rejection) instead of SSE
      const contentType = res.headers.get("Content-Type") || "";
      if (!contentType.includes("text/event-stream")) {
        const json = await res.json();
        const msg = json?.data ?? json?.message ?? JSON.stringify(json);
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", text: msg };
          return updated;
        });
        setIsStreaming(false);
        setIsWaiting(false);
        scrollToBottom();
        return;
      }

      // Read historyId from response header if provided
      const headerHistoryId = res.headers.get("X-History-Id");
      if (headerHistoryId) setLocalHistoryId(headerHistoryId);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";
      let firstChunk = true;
      let pendingEventType = null;
      let lineBuffer = "";

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        if (firstChunk) {
          setIsWaiting(false);
          firstChunk = false;
        }

        lineBuffer += decoder.decode(value, { stream: true });
        const lines = lineBuffer.split("\n");
        lineBuffer = lines.pop();

        for (const line of lines) {
          if (line.startsWith("event: ")) {
            pendingEventType = line.slice(7).trim();
          } else if (line.startsWith("data: ")) {
            const data = line.slice(6).trim();
            if (pendingEventType === "historyid") {
              setLocalHistoryId(data);
              pendingEventType = null;
            } else if (data === "[DONE]") {
              pendingEventType = null;
              break;
            } else {
              pendingEventType = null;
              try {
                const parsed = JSON.parse(data);
                if (parsed?.historyid) setLocalHistoryId(parsed.historyid);
                if (parsed?.done) break;
                const token =
                  parsed?.chunk ??
                  parsed?.choices?.[0]?.delta?.content ??
                  parsed?.content ??
                  parsed?.text ??
                  "";
                accumulated += token;
              } catch {
                accumulated += data;
              }
            }
          }
        }

        // Update the last (assistant) message with accumulated text
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = { role: "assistant", text: accumulated };
          return updated;
        });
        scrollToBottom();
      }
    } catch (err) {
      if (err.name !== "AbortError") {
        setError("連線錯誤，請再試。");
      }
    } finally {
      setIsStreaming(false);
      setIsWaiting(false);
      abortRef.current = null;
    }
  }, [question, westernDateTime, isMale, localHistoryId, historyIdProp, isStreaming]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      handleSubmit();
    }
  };

  return (
    <div className={styles.container}>
      {/* Chat history */}
      {(messages.length > 0 || isWaiting || error) && (
        <div className={styles.messagesContainer}>
          {messages.map((msg, i) => (
            <div key={i} className={msg.role === "user" ? styles.userRow : styles.assistantRow}>
              <div className={msg.role === "user" ? styles.userBubble : styles.assistantBubble}>
                {msg.role === "assistant" && !msg.text && isWaiting ? (
                  <span className={styles.waiting}>請稍後…</span>
                ) : (
                  <pre className={styles.bubbleText}>
                    <RenderText text={msg.text} />
                    {msg.role === "assistant" && isStreaming && i === messages.length - 1 && (
                      <span className={styles.cursor}>▌</span>
                    )}
                  </pre>
                )}
              </div>
            </div>
          ))}
          {error && (
            <div className={styles.assistantRow}>
              <div className={styles.assistantBubble}>
                <span className={styles.error}>{error}</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      )}
      {/* Input row */}
      <div className={styles.inputRow}>
        <textarea
          className={styles.questionInput}
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="請輸入問題… (Ctrl+Enter 發送)"
          rows={2}
          disabled={isStreaming}
        />
        <div className={styles.actionButtons}>
          <IconButton
            onClick={handleNewChat}
            disabled={isStreaming}
            color="default"
            title="新對話"
          >
            <AddCommentIcon />
          </IconButton>
          <IconButton
            onClick={handleSubmit}
            disabled={isStreaming || !question.trim()}
            color="primary"
            title="發送"
          >
            <SendIcon />
          </IconButton>
        </div>
      </div>
    </div>
  );
}
